import LanguageChanger from "@/components/LanguageChanger/languageChanger.component";
import { sendMagicLink } from "@/models/socket/authenticate.model";
import { useSocket } from "@/services/Socket/socketContext";
import { Button } from "@/shadcn/components/ui/button";
import { Input } from "@/shadcn/components/ui/input";
import { Label } from "@/shadcn/components/ui/label";
import { isLanguageSupportEnabled } from "@/utils/utils.util";
import { validateEmail } from "@/utils/validation.util";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
const languageSupportEnabled = isLanguageSupportEnabled();

type MagicLinkProps = {
  email: string;
  setEmail: (email: string) => void;
};

const MagicLink: React.FC<MagicLinkProps> = ({ email, setEmail }) => {
  const socketController = useSocket();
  const { t } = useTranslation();
  const [error, setError] = useState<string>("");
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [submitSuccess, setSubmitSuccess] = useState<boolean>(false);
  const [loginCount, setLoginCount] = useState(0);
  const [loginCoolDown, setLoginCoolDown] = useState(false);
  const [countdown, setCountdown] = useState(0);

  // ------------------------------------------------------------
  // UseEffects handle cooldown to prevent server spamming

  // handles login count
  // up to 3 is allowed, equal to 3 sets 10 second cooldown, more than three sets 60 second cooldown
  useEffect(() => {
    if (loginCount === 3) {
      // Start with 10 seconds countdown
      setCountdown(10);
      setLoginCoolDown(true);
    } else if (loginCount > 3) {
      // Start with 60 seconds countdown
      setCountdown(60);
      setLoginCoolDown(true);
    } else {
      setLoginCoolDown(false);
    }
  }, [loginCount]);

  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (countdown > 0) {
      // Decrement countdown every second
      timer = setInterval(() => {
        setCountdown((c) => c - 1);
      }, 1000);
    } else {
      setLoginCoolDown(false);
    }

    // ------------------------------------------------------------

    // Cleanup function to clear the interval
    return () => clearInterval(timer);
  }, [countdown]);

  function validateMagicLinkForm(): { email: string } | false {
    const strippedEmail = email.replace(/\s/g, "");
    if (!strippedEmail) {
      setError(t("Please enter a valid email"));
      return false;
    }

    if (!validateEmail(strippedEmail)) {
      setError(t("Please enter a valid email"));
      return false;
    }

    return { email: strippedEmail };
  }

  async function handleSubmit() {
    setError("");
    const formData = validateMagicLinkForm();
    if (!formData) {
      return;
    }
    setSubmitting(true);
    // Simulate API call
    try {
      const response = await sendMagicLink(formData.email);
      setLoginCount((prevCount) => prevCount + 1);

      if (response && response.rStatus) {
        if (response.rStatus === "error") {
          // Error Handling
          if (response.rData && response.rData !== "") {
            setError(response.rData);
          } else {
            // Hit here means model error - its not sending a message and needs to be fixed
            console.error("Magic Link Default Error Response called");
            setError(t("Magic Link General Error Response"));
          }
          setSubmitting(false);
          return;
        } else {
          // Success
          setEmail("");
          setError("");
          setSubmitSuccess(true);
          setSubmitting(false);
          setCountdown(0);
          setLoginCount(0);
          setLoginCoolDown(false);
          return;
        }
      } else {
        setError(t("An unknown error occurred"));
        setSubmitting(false);
        return;
      }
    } catch (error) {
      console.error("Socket Error occured for Magic Link Send as:", error);
      setError(t("Magic Link General Error Response"));
      setSubmitting(false);
      return;
    }
  }

  return (
    <>
      {!submitSuccess && (
        <div className="grid w-full gap-2">
          <div className="grid grid-cols-2 items-center">
            <Label htmlFor="email" className="m-auto text-base font-semibold">
              {t("Email")}
            </Label>
            <Input
              type="text"
              className="w-full p-1 ps-2"
              id="email"
              placeholder={t("Enter email")}
              value={email}
              onChange={(e) => {
                setError("");
                setEmail(e.target.value);
              }}
              autoCapitalize="off"
              disabled={submitting}
            />
          </div>
          {languageSupportEnabled && (
            <div className="grid grid-cols-2 items-center">
              <Label
                htmlFor="language-changer"
                className="m-auto text-base font-semibold"
              >
                {t("Language")}
              </Label>
              <LanguageChanger />
            </div>
          )}
        </div>
      )}
      {
        // Display error message if there is any
        error && <div className="mt-5 text-balance text-red-500">{error}</div>
      }
      {
        // Display success message if the form is submitted successfully
        submitSuccess && (
          <div className="mt-5 text-green-500">
            {t("Magic link sent! Please check your Email inbox")}
          </div>
        )
      }
      {!submitSuccess && (
        <Button
          onClick={handleSubmit}
          className="mt-5"
          disabled={email === "" || loginCoolDown}
        >
          {submitting
            ? t("Submitting...")
            : loginCoolDown
              ? `${t("Submit Cooldown: ")} ${countdown}s`
              : t("Submit")}
        </Button>
      )}
    </>
  );
};

export default MagicLink;
